import React from 'react';
import { Card, Progress, Statistic, Row, Col, Alert, Button, Spin, Tag, Space, theme } from 'antd';
import {
  DatabaseOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useStorageCapacity } from '@/hooks/useStorageCapacity';
import { logger } from '../../utils/logger';

/**
 * ストレージダッシュボード
 * 容量使用状況、プラン情報、コスト削減効果を表示
 */
export function StorageDashboard() {
  const { data: capacity, isLoading, error } = useStorageCapacity();
  const { token } = theme.useToken();
  const [, setShowAddonModal] = React.useState(false);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 0' }}>
        <Spin size="large" tip="容量情報を読み込み中...">
          <div style={{ minHeight: 48 }} />
        </Spin>
      </div>
    );
  }

  if (error || !capacity) {
    return (
      <Alert
        type="error"
        message="容量情報の取得に失敗しました"
        description={error?.message}
      />
    );
  }

  const { subscription, usage, alerts } = capacity;
  const usedGB = Number(usage.totalGB) || 0;
  const remainingGB = Number(usage.remainingGB);
  const limitGB = Number(subscription.totalLimitGB) || 0;
  const hasRemaining = Number.isFinite(remainingGB);
  const rawUsedGB = hasRemaining && limitGB > 0 ? Math.max(0, limitGB - remainingGB) : usedGB;
  const displayUsedGB = Math.ceil(rawUsedGB * 10) / 10;
  const displayRemainingGB = Math.max(0, limitGB - displayUsedGB);
  const usagePercent = limitGB > 0 ? (displayUsedGB / limitGB) * 100 : 0;
  const status = usagePercent >= 95 ? 'exception' : usagePercent >= 80 ? 'active' : 'normal';

  // デバッグ情報
  logger.debug('[StorageDashboard] Capacity data:', {
    baseStorageGB: subscription.baseStorageGB,
    addonsCount: subscription.addons.length,
    addons: subscription.addons,
    totalLimitGB: subscription.totalLimitGB,
    addonTotalPrice: subscription.addonTotalPrice,
  });

  return (
    <div className="storage-dashboard">
      {/* プラン情報 */}
      <Card
        title={
          <Space>
            <DatabaseOutlined />
            <span>ストレージ容量管理</span>
          </Space>
        }
        extra={
          <Tag color="blue" style={{ fontSize: 14 }}>
            {subscription.plan.toUpperCase()} プラン
          </Tag>
        }
      >
        {/* 容量使用状況 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 16, fontWeight: 500 }}>総容量使用状況</span>
            <span style={{ fontSize: 14, color: token.colorTextSecondary }}>
              {displayUsedGB.toFixed(1)} GB / {subscription.totalLimitGB} GB
            </span>
          </div>
          <Progress
            percent={usagePercent}
            status={status}
            size={{ height: 20 }}
            format={(percent) => `${percent?.toFixed(1)}%`}
          />
        </div>

        {/* 統計情報 */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ backgroundColor: token.colorInfoBg }}>
              <Statistic
                title="使用中"
                value={displayUsedGB}
                suffix="GB"
                precision={1}
                prefix={<DatabaseOutlined style={{ color: token.colorInfo }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ backgroundColor: usagePercent > 90 ? token.colorErrorBg : token.colorSuccessBg }}>
              <Statistic
                title="残り容量"
                value={displayRemainingGB}
                suffix="GB"
                precision={1}
                valueStyle={{ color: usagePercent > 90 ? token.colorError : token.colorSuccess }}
                prefix={usagePercent > 90 ? <WarningOutlined /> : null}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ backgroundColor: token.colorFillQuaternary }}>
              <Statistic
                title="ファイル数"
                value={usage.totalFiles}
                suffix="件"
              />
            </Card>
          </Col>
        </Row>

        {/* 追加オプション情報 */}
        {subscription.addons.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, backgroundColor: token.colorInfoBg, borderRadius: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
              📦 アクティブな追加オプション
            </div>
            {subscription.addons.map((addon) => (
              <div key={addon.id} style={{ fontSize: 13 }}>
                容量追加オプション ×{addon.quantity}個: +{addon.totalCapacityGB} GB
                （¥{addon.totalPriceJPY.toLocaleString()}/月）
              </div>
            ))}
          </div>
        )}

        {/* プラン詳細 */}
        <div style={{ marginTop: 16, padding: 12, backgroundColor: token.colorFillQuaternary, borderRadius: 4 }}>
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ fontSize: 12, color: token.colorTextSecondary }}>基本容量</div>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>{subscription.baseStorageGB} GB</div>
            </Col>
            {subscription.addons.length > 0 && (
              <Col span={12}>
                <div style={{ fontSize: 12, color: token.colorTextSecondary }}>追加オプション容量</div>
                <div style={{ fontSize: 16, fontWeight: 'bold', color: token.colorSuccess }}>
                  +{subscription.totalLimitGB - subscription.baseStorageGB} GB
                </div>
              </Col>
            )}
          </Row>
        </div>

        {/* アラート表示 */}
        {alerts.map((alert, i) => (
          <Alert
            key={i}
            type={alert.level === 'critical' ? 'error' : alert.level === 'warning' ? 'warning' : 'info'}
            message={alert.message}
            description={alert.action}
            showIcon
            style={{ marginTop: 16 }}
            action={
              alert.action?.includes('追加オプション') && (
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddonModal(true)}
                >
                  容量を追加
                </Button>
              )
            }
          />
        ))}
      </Card>

      {/* ファイルタイプ別内訳 */}
      <Card
        title="ファイルタイプ別内訳"
        style={{ marginTop: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="📷 写真"
              value={usage.breakdown.photos.sizeGB}
              suffix="GB"
              precision={1}
            />
            <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 4 }}>
              {usage.breakdown.photos.count.toLocaleString()}枚
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="🎥 動画"
              value={usage.breakdown.videos.sizeGB}
              suffix="GB"
              precision={1}
            />
            <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 4 }}>
              {usage.breakdown.videos.count.toLocaleString()}本
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="📄 文書"
              value={usage.breakdown.documents.sizeGB}
              suffix="GB"
              precision={1}
            />
            <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 4 }}>
              {usage.breakdown.documents.count.toLocaleString()}件
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="📦 その他"
              value={usage.breakdown.other.sizeGB}
              suffix="GB"
              precision={1}
            />
            <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 4 }}>
              {usage.breakdown.other.count.toLocaleString()}件
            </div>
          </Col>
        </Row>
      </Card>


      {/* 追加オプション購入モーダル */}
      {/* TODO: StorageAddonPurchaseModal コンポーネント */}
    </div>
  );
}
